import { useEffect, useMemo, useState } from 'react';
import omit from 'lodash/omit';
import { User } from '@/types/api';
import InterviewNoteComponent from './InterviewNote.component';
import { CustomNoteType } from '../InterviewNotesManager/InterviewNotesManager.component';

export type InterviewNoteProps = {
  id?: string;
  note?: string;
  index: number;
  author?: User;
  publishedAt?: string;
  isActionLoading: boolean;
  onCancel: (interviewNoteIndex: number) => void;
  onSave: (index: number, newNote: CustomNoteType) => Promise<boolean>;
  onDelete: (interviewNoteId: string) => Promise<boolean>;
};

type EditingNote = {
  id: string;
  author: User | undefined;
  publishedAt: Date | undefined;
  note: string;
  prevNote: string;
  isEditing: boolean;
};

export default function InterviewNote({
  index,
  id = '',
  note = '',
  isActionLoading,
  author,
  publishedAt,
  onSave,
  onDelete,
  onCancel,
}: InterviewNoteProps): JSX.Element {
  const [editingNote, setEditingNote] = useState<EditingNote>(() => {
    return {
      id,
      author,
      publishedAt: publishedAt ? new Date(publishedAt) : undefined,
      note,
      prevNote: note,
      isEditing: !id,
    };
  });

  useEffect(() => {
    setEditingNote((prev) => ({
      ...prev,
      note,
      id,
      publishedAt: publishedAt ? new Date(publishedAt) : undefined,
    }));
  }, [id, note, publishedAt]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const onToggleIsEditing = (): void => {
    setEditingNote((prev) => ({
      ...prev,
      isEditing: !prev.isEditing,
    }));
  };

  const onChangeNote = (value: string): void => {
    setEditingNote((prev) => ({
      ...prev,
      note: value,
    }));
  };

  const onCancelEdition = (triggerCallback = true): void => {
    setEditingNote((prev) => ({
      ...prev,
      note: prev.prevNote,
    }));

    if (triggerCallback && !publishedAt) {
      onCancel(index);
    }

    onToggleIsEditing();
  };

  const getFormattedNote = (noteToFormat: EditingNote): any => {
    return {
      ...omit(noteToFormat, ['author', 'publishedAt', 'isEditing', 'prevNote']),
      user: noteToFormat.author,
    };
  };

  const onSaveNote = async (): Promise<void> => {
    const newNote = {
      ...editingNote,
      prevNote: editingNote.note,
      publishedAt: new Date(),
      author,
    };

    const isSaved = await onSave(index, getFormattedNote(newNote));

    if (isSaved) {
      setEditingNote((prev) => ({
        ...prev,
        ...newNote,
      }));
      onToggleIsEditing();
    } else {
      onCancelEdition(false);
    }
  };

  const onToggleDeleteModal = (event?: any): void => {
    event?.stopPropagation();
    setIsDeleteModalOpen((prev) => !prev);
  };

  const onDeleteNote = async (): Promise<void> => {
    onToggleDeleteModal();

    const isDeleted = await onDelete(editingNote.id);

    if (isDeleted) {
      onToggleIsEditing();
    }
  };

  const canSave = useMemo(() => {
    const noteIsNotEmpty = !!editingNote.note;
    const isPublished = !!editingNote.publishedAt;

    return noteIsNotEmpty || isPublished;
  }, [editingNote.note, editingNote.publishedAt]);

  return (
    <InterviewNoteComponent
      isActionLoading={isActionLoading}
      isInitialNote={index === 0}
      author={editingNote.author}
      publishedAt={editingNote.publishedAt}
      isEditing={editingNote.isEditing}
      note={editingNote.note}
      canSave={canSave}
      onChangeNote={onChangeNote}
      onToggleIsEditing={onToggleIsEditing}
      onSaveNote={onSaveNote}
      onDeleteNote={onDeleteNote}
      isDeleteModalOpen={isDeleteModalOpen}
      onToggleDeleteModal={onToggleDeleteModal}
      onCancelEdition={onCancelEdition}
    />
  );
}
